import _asyncToGenerator from "/vercel/workpath0/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import _classCallCheck from "/vercel/workpath0/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/vercel/workpath0/node_modules/@babel/runtime/helpers/esm/createClass";
import _inherits from "/vercel/workpath0/node_modules/@babel/runtime/helpers/esm/inherits";
import _possibleConstructorReturn from "/vercel/workpath0/node_modules/@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "/vercel/workpath0/node_modules/@babel/runtime/helpers/esm/getPrototypeOf";
import _regeneratorRuntime from "/vercel/workpath0/node_modules/@babel/runtime/regenerator";

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

/**
 * @license
 * Copyright 2019 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * =============================================================================
 */
import { env, tensor, util } from '@tensorflow/tfjs-core';
import { LazyIterator } from './lazy_iterator';
/**
 * Provide a stream of tensors from microphone audio stream. The tensors are
 * representing audio data as frequency-domain spectrogram generated with
 * browser's native FFT. Tensors representing time-domain waveform is available
 * based on configuration. Only works in browser environment.
 */

export var MicrophoneIterator = /*#__PURE__*/function (_LazyIterator) {
  _inherits(MicrophoneIterator, _LazyIterator);

  var _super = _createSuper(MicrophoneIterator);

  function MicrophoneIterator(microphoneConfig) {
    var _this;

    _classCallCheck(this, MicrophoneIterator);

    _this = _super.call(this);
    _this.microphoneConfig = microphoneConfig;
    _this.isClosed = false;
    _this.fftSize = microphoneConfig.fftSize || 1024;
    var fftSizeLog2 = Math.log2(_this.fftSize);

    if (_this.fftSize < 0 || fftSizeLog2 < 4 || fftSizeLog2 > 14 || !Number.isInteger(fftSizeLog2)) {
      throw new Error("Invalid fftSize: it must be a power of 2 between " + "2 to 4 and 2 to 14, but got ".concat(_this.fftSize));
    }

    _this.numFrames = microphoneConfig.numFramesPerSpectrogram || 43;
    _this.sampleRateHz = microphoneConfig.sampleRateHz;
    _this.columnTruncateLength = microphoneConfig.columnTruncateLength || _this.fftSize;
    _this.audioTrackConstraints = microphoneConfig.audioTrackConstraints;
    _this.smoothingTimeConstant = microphoneConfig.smoothingTimeConstant || 0;
    _this.includeSpectrogram = microphoneConfig.includeSpectrogram === false ? false : true;
    _this.includeWaveform = microphoneConfig.includeWaveform === true ? true : false;

    if (!_this.includeSpectrogram && !_this.includeWaveform) {
      throw new Error('Both includeSpectrogram and includeWaveform are false. ' + 'At least one type of data should be returned.');
    }

    return _this;
  }

  _createClass(MicrophoneIterator, [{
    key: "summary",
    value: function summary() {
      return "microphone";
    } // Construct a MicrophoneIterator and start the audio stream.

  }, {
    key: "start",
    value: // Start the audio stream and FFT.
    function () {
      var _start = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var ctxConstructor, streamSource;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.prev = 0;
                _context.next = 3;
                return navigator.mediaDevices.getUserMedia({
                  audio: this.audioTrackConstraints == null ? true : this.audioTrackConstraints,
                  video: false
                });

              case 3:
                this.stream = _context.sent;
                _context.next = 9;
                break;

              case 6:
                _context.prev = 6;
                _context.t0 = _context["catch"](0);
                throw new Error("Error thrown while initializing video stream: ".concat(_context.t0.message));

              case 9:
                if (this.stream) {
                  _context.next = 11;
                  break;
                }

                throw new Error('Could not obtain audio from microphone.');

              case 11:
                ctxConstructor = // tslint:disable-next-line:no-any
                window.AudioContext || window.webkitAudioContext;
                this.audioContext = new ctxConstructor();

                if (this.sampleRateHz) {
                  _context.next = 17;
                  break;
                }

                // If sample rate is not provided, use the available sample rate on
                // device.
                this.sampleRateHz = this.audioContext.sampleRate;
                _context.next = 19;
                break;

              case 17:
                if (!(this.audioContext.sampleRate !== this.sampleRateHz)) {
                  _context.next = 19;
                  break;
                }

                throw new Error("Mismatch in sampling rate: " + "Expected: ".concat(this.sampleRateHz, "; ") + "Actual: ".concat(this.audioContext.sampleRate));

              case 19:
                streamSource = this.audioContext.createMediaStreamSource(this.stream);
                this.analyser = this.audioContext.createAnalyser();
                this.analyser.fftSize = this.fftSize * 2;
                this.analyser.smoothingTimeConstant = this.smoothingTimeConstant;
                streamSource.connect(this.analyser);
                this.freqData = new Float32Array(this.fftSize);
                this.timeData = new Float32Array(this.fftSize);
                return _context.abrupt("return");

              case 27:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this, [[0, 6]]);
      }));

      function start() {
        return _start.apply(this, arguments);
      }

      return start;
    }()
  }, {
    key: "next",
    value: function () {
      var _next = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        var spectrogramTensor, waveformTensor, audioDataQueue, freqData, timeData;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                if (!this.isClosed) {
                  _context2.next = 2;
                  break;
                }

                return _context2.abrupt("return", {
                  value: null,
                  done: true
                });

              case 2:
                _context2.next = 4;
                return this.getAudioData();

              case 4:
                audioDataQueue = _context2.sent;

                if (this.includeSpectrogram) {
                  freqData = this.flattenQueue(audioDataQueue.freqDataQueue);
                  spectrogramTensor = this.getTensorFromAudioDataArray(freqData, [this.numFrames, this.columnTruncateLength, 1]);
                }

                if (this.includeWaveform) {
                  timeData = this.flattenQueue(audioDataQueue.timeDataQueue);
                  waveformTensor = this.getTensorFromAudioDataArray(timeData, [this.numFrames * this.fftSize, 1]);
                }

                return _context2.abrupt("return", {
                  value: {
                    'spectrogram': spectrogramTensor,
                    'waveform': waveformTensor
                  },
                  done: false
                });

              case 8:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      function next() {
        return _next.apply(this, arguments);
      }

      return next;
    }() // Capture one result from the audio stream, and extract the value from
    // iterator.next() result.

  }, {
    key: "capture",
    value: function () {
      var _capture = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.next = 2;
                return this.next();

              case 2:
                return _context3.abrupt("return", _context3.sent.value);

              case 3:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, this);
      }));

      function capture() {
        return _capture.apply(this, arguments);
      }

      return capture;
    }()
  }, {
    key: "getAudioData",
    value: function () {
      var _getAudioData = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        var _this2 = this;

        var freqDataQueue, timeDataQueue, currentFrames;
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                freqDataQueue = [];
                timeDataQueue = [];
                currentFrames = 0;
                return _context4.abrupt("return", new Promise(function (resolve) {
                  var intervalID = setInterval(function () {
                    if (_this2.includeSpectrogram) {
                      _this2.analyser.getFloatFrequencyData(_this2.freqData); // If the audio stream is initializing, return empty queue.


                      if (_this2.freqData[0] === -Infinity) {
                        resolve({
                          freqDataQueue: freqDataQueue,
                          timeDataQueue: timeDataQueue
                        });
                      }

                      freqDataQueue.push(_this2.freqData.slice(0, _this2.columnTruncateLength));
                    }

                    if (_this2.includeWaveform) {
                      _this2.analyser.getFloatTimeDomainData(_this2.timeData);

                      timeDataQueue.push(_this2.timeData.slice());
                    } // Clean interval and return when all frames have been collected


                    if (++currentFrames === _this2.numFrames) {
                      clearInterval(intervalID);
                      resolve({
                        freqDataQueue: freqDataQueue,
                        timeDataQueue: timeDataQueue
                      });
                    }
                  }, _this2.fftSize / _this2.sampleRateHz * 1e3);
                }));

              case 4:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4);
      }));

      function getAudioData() {
        return _getAudioData.apply(this, arguments);
      }

      return getAudioData;
    }() // Stop the audio stream and pause the iterator.

  }, {
    key: "stop",
    value: function stop() {
      if (!this.isClosed) {
        this.isClosed = true;
        this.analyser.disconnect();
        this.audioContext.close();

        if (this.stream != null && this.stream.getTracks().length > 0) {
          this.stream.getTracks()[0].stop();
        }
      }
    } // Override toArray() function to prevent collecting.

  }, {
    key: "toArray",
    value: function toArray() {
      throw new Error('Can not convert infinite audio stream to array.');
    } // Return audio sampling rate in Hz

  }, {
    key: "getSampleRate",
    value: function getSampleRate() {
      return this.sampleRateHz;
    }
  }, {
    key: "flattenQueue",
    value: function flattenQueue(queue) {
      var frameSize = queue[0].length;
      var freqData = new Float32Array(queue.length * frameSize);
      queue.forEach(function (data, i) {
        return freqData.set(data, i * frameSize);
      });
      return freqData;
    }
  }, {
    key: "getTensorFromAudioDataArray",
    value: function getTensorFromAudioDataArray(freqData, shape) {
      var vals = new Float32Array(util.sizeFromShape(shape)); // If the data is less than the output shape, the rest is padded with zeros.

      vals.set(freqData, vals.length - freqData.length);
      return tensor(vals, shape);
    }
  }], [{
    key: "create",
    value: function () {
      var _create = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        var microphoneConfig,
            microphoneIterator,
            _args5 = arguments;
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                microphoneConfig = _args5.length > 0 && _args5[0] !== undefined ? _args5[0] : {};

                if (!env().get('IS_NODE')) {
                  _context5.next = 3;
                  break;
                }

                throw new Error('microphone API is only supported in browser environment.');

              case 3:
                microphoneIterator = new MicrophoneIterator(microphoneConfig); // Call async function start() to initialize the audio stream.

                _context5.next = 6;
                return microphoneIterator.start();

              case 6:
                return _context5.abrupt("return", microphoneIterator);

              case 7:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5);
      }));

      function create() {
        return _create.apply(this, arguments);
      }

      return create;
    }()
  }]);

  return MicrophoneIterator;
}(LazyIterator);